/**
 * Step 2 — what it means for them (village rebuild, 21 Oct 2026; the agreed mock-up's j1-step2).
 *
 * Staying put in four numbers, with "See the year-by-year figures, staying put" beneath: the baseline
 * every plan on step 3 is measured against. Then WHERE THEY START, worked out from the file: their
 * savings and investments against the entry payment plus the money kept for emergencies, so whether
 * keeping the home is possible at all, and how much is left to put to work if they sell. Then what the
 * move changes, and how long the money lasts staying put.
 *
 * ONE SOURCE, as before: the pension and cashflow are today's assessment (`todayFinancials`), the
 * estate and the runway the stay-put projection — the same run the engine's own baseline makes.
 */
import React from 'react';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { householdVoice } from '../../utils/householdWords.ts';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { FundingRunway } from '../../components/narrative/FundingRunway.tsx';
import type { PlanRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { RATES_PROVENANCE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { VillageInputs } from './RetirementVillagePage.tsx';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { MeansTestBeats } from '../../components/insights/MeansTestPosition.tsx';
import { getPersonalLiquidAssetsForRad } from '../../utils/entityFundingUtils.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../constants/index.ts';
import { benchmarkWords } from '../../utils/benchmarkWords.ts';

/** Where they start: what they have to pay with, and what selling would leave. */
export interface RvStartingPoint {
    hasHome: boolean;
    savings: number;
    reserve: number;
    entryFee: number;
    /** Savings and investments less the entry payment and the reserve; negative when short. */
    savingsAfterEntry: number;
    /** 'yes' — comfortably; 'just' — within $50,000; 'no' — not without selling. */
    keepHome: 'yes' | 'just' | 'no' | null;
    saleNet: number;
    /** What selling leaves after the entry payment. */
    afterSale: number;
}

export function rvStartingPoint(factFind: DraftFactFind, entryFee: number, reserveOverride?: number | null): RvStartingPoint {
    const num = (x: unknown) => parseFloat(String(x ?? '')) || 0;
    const hasHome = num(factFind?.home?.value) > 0;
    const savings = getPersonalLiquidAssetsForRad(factFind).reduce((t, a) => t + num((a as { value?: unknown }).value), 0);
    const reserve = reserveOverride != null && reserveOverride >= 0 ? reserveOverride : getRecommendedEmergencyFund(factFind);
    const savingsAfterEntry = savings - entryFee - reserve;
    const homeValue = num(factFind?.home?.value);
    const saleNet = hasHome ? Math.round(homeValue - homeValue * 0.025) : 0;
    const keepHome = !hasHome ? null : savingsAfterEntry < 0 ? 'no' : savingsAfterEntry < 50_000 ? 'just' : 'yes';
    return { hasHome, savings, reserve, entryFee, savingsAfterEntry, keepHome, saleNet, afterSale: Math.max(0, saleNet - entryFee) };
}

interface RvStep2Props {
    factFind: DraftFactFind;
    todayFinancials: { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null;
    stayPutLegacy: number | null;
    runway: PlanRunwayCopy | null;
    legacyYears: number;
    villageInputs: VillageInputs;
    annualOngoing: number;
    reserve: number;
    onLegacyYearsChange?: (years: number) => void;
    onBack: () => void;
    onNext: () => void;
    onAssumptions: () => void;
    onShowProjections: () => void;
}

const card = 'rounded-2xl border border-slate-200 bg-white p-6 shadow-card';

const Tile = ({ label, value, unit, caption, tone }: { label: string; value: string; unit?: string; caption: React.ReactNode; tone?: 'good' | 'bad' }) => (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-card">
        <p className="text-[12.5px] font-semibold text-slate-500">{label}</p>
        <p className={`mt-1 text-[26px] font-bold leading-none tabular-nums tracking-tight ${tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>
            {value}{unit && <span className="ml-1 text-[13px] font-semibold text-slate-500">{unit}</span>}
        </p>
        <p className="mt-2 text-[12.5px] leading-snug text-slate-500">{caption}</p>
    </div>
);

const RvStep2 = ({ factFind, todayFinancials, stayPutLegacy, runway, legacyYears, villageInputs, annualOngoing, reserve, onLegacyYearsChange, onBack, onNext, onAssumptions, onShowProjections }: RvStep2Props) => {
    const v = householdVoice(factFind);
    const ready = !!todayFinancials;
    const pension = todayFinancials?.socialSecurity ?? 0;
    const cashFlow = todayFinancials?.cashFlow ?? 0;
    const netWorth = todayFinancials?.netWorth ?? 0;
    const years = Number(legacyYears) || 0;
    const entryFee = villageInputs.entryFee || 0;
    const sp = rvStartingPoint(factFind, entryFee, reserve);
    const stay = sp.hasHome ? 'staying put' : 'renting on';
    const [showWhy, setShowWhy] = React.useState(false);

    return (
        <div className="space-y-5" data-testid="rv-step2">
            <div>
                <h2 className="text-[24px] font-bold tracking-tight text-brand-navy">{sp.hasHome ? `If ${v.names} stay${v.s} where ${v.they} are` : `If ${v.names} keep${v.s} renting`}</h2>
                <p className="mt-1 text-[15px] text-slate-600">Every way of moving on the next step is measured against these four numbers, which the cards call &ldquo;{benchmarkWords('village', { rents: !sp.hasHome }).word}&rdquo;.</p>
            </div>
            {!ready ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-[14px] text-slate-600">Working out today&apos;s position…</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="rv-four-numbers">
                        <Tile label="Age Pension" value={formatCurrency(Math.round(pension))} unit="a year" caption={<>{v.couple ? 'the two of them' : 'a year'} · <button type="button" onClick={() => setShowWhy((o) => !o)} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2">why?</button></>} />
                        <Tile label={cashFlow < 0 ? 'Short each year' : 'Left over each year'} value={formatCurrency(Math.abs(Math.round(cashFlow)))} tone={cashFlow < 0 ? 'bad' : 'good'} caption={cashFlow < 0 ? 'money in, less money out; met from savings' : 'money in, less money out'} />
                        <Tile label={`What ${v.they} own`} value={formatCurrency(Math.round(netWorth))} caption={`less what ${v.they} owe`} />
                        <Tile label="Left to the family" value={stayPutLegacy != null ? formatCurrency(Math.round(stayPutLegacy)) : '—'} caption={stayPutLegacy != null ? (onLegacyYearsChange ? <ProjectionYearsInput years={years} onChange={onLegacyYearsChange} after={` ${stay}.`} /> : `after about ${years} years`) : 'not available: the projection could not be run'} />
                    </div>
                    <p className="text-[14px]">
                        <button type="button" onClick={onShowProjections} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="rv-step2-year-by-year">See the year-by-year figures, {stay}</button>
                        <span className="text-slate-500"> — Age Pension, money in and out, and what {v.they} own, each year for about {years} years. Every plan on the next step is measured against these.</span>
                    </p>
                    {showWhy && (
                        <div className={card}>
                            <PlainWordsCard eyebrow={`Why ${v.possessive} Age Pension is ${formatCurrency(Math.round(pension))}`} className="-m-5 border-0 bg-transparent shadow-none">
                                <MeansTestBeats careDepositRelevant={false} financials={todayFinancials} isCouple={factFind?.adviceFor === 'couple'} name={v.names} alsoMoves={<>changes what the village move costs the pension</>} />
                            </PlainWordsCard>
                        </div>
                    )}
                </>
            )}

            {entryFee > 0 && (
                <div className={card} data-testid="rv-starting-point">
                    <p className="text-[12px] font-semibold text-brand-navy">Where {v.they} start</p>
                    <table className="mt-1 w-full text-[14px]">
                        <tbody>
                            {!sp.hasHome && <tr className="border-b border-slate-100"><td className="py-2 text-slate-700">No home to keep or sell</td><td className="py-2 text-right text-slate-500">the rent stops and the village fees start</td></tr>}
                            <tr className="border-b border-slate-100"><td className="py-2 text-slate-700">Savings and investments</td><td className="py-2 text-right font-bold tabular-nums text-slate-900">{formatCurrency(Math.round(sp.savings))}</td></tr>
                            <tr className="border-b border-slate-100"><td className="py-2 text-slate-700">Entry payment, plus {formatCurrency(Math.round(sp.reserve))} kept for emergencies</td><td className="py-2 text-right tabular-nums text-slate-700">{formatCurrency(Math.round(entryFee + sp.reserve))}</td></tr>
                            {sp.hasHome ? (
                                <>
                                    <tr className="border-b border-slate-100">
                                        <td className="py-2 font-semibold text-slate-900">Keeping the home and paying from savings and investments</td>
                                        <td className="py-2 text-right" data-testid="rv-keep-home-verdict">
                                            {sp.keepHome === 'yes' && <><b className="text-emerald-700">Possible</b> <span className="text-slate-500">— {formatCurrency(Math.round(sp.savingsAfterEntry))} left over</span></>}
                                            {sp.keepHome === 'just' && <><b className="text-amber-700">Possible, just</b> <span className="text-slate-500">— {sp.savingsAfterEntry < 1000 ? 'nothing left over' : `${formatCurrency(Math.round(sp.savingsAfterEntry))} left over`}</span></>}
                                            {sp.keepHome === 'no' && <><b className="text-rose-700">Not without selling</b> <span className="text-slate-500">— {formatCurrency(Math.round(-sp.savingsAfterEntry))} short</span></>}
                                        </td>
                                    </tr>
                                    <tr><td className="py-2 text-slate-700">If {v.they} sell the home: about {formatCurrency(sp.saleNet)} after selling costs, less the entry payment</td><td className="py-2 text-right font-bold tabular-nums text-slate-900">about {formatCurrency(Math.round(sp.afterSale / 10_000) * 10_000)} to put to work</td></tr>
                                </>
                            ) : (
                                <tr><td className="py-2 font-semibold text-slate-900">Left to put to work after the move</td><td className="py-2 text-right font-bold tabular-nums text-slate-900">{sp.savingsAfterEntry >= 0 ? formatCurrency(Math.round(sp.savingsAfterEntry)) : <span className="text-rose-700">{formatCurrency(Math.round(-sp.savingsAfterEntry))} short</span>}</td></tr>
                            )}
                        </tbody>
                    </table>
                    <p className="mt-2 text-[12.5px] text-slate-500">Worked out from {v.possessive} file.</p>

                    <p className="mt-5 text-[12px] font-semibold text-brand-navy">What the move changes</p>
                    <ul className="mt-1 list-disc space-y-1.5 pl-5 text-[15px] text-slate-700">
                        <li><strong className="text-slate-900">{formatCurrency(entryFee)} has to be paid on the day {v.they} move.</strong></li>
                        <li><strong className="text-slate-900">{formatCurrency(Math.round(annualOngoing))} a year in fees</strong>, from the day {v.they} move{sp.hasHome ? '' : ', and the rent stops'}.</li>
                        {sp.hasHome ? (
                            <li><strong className="text-slate-900">The Age Pension changes, whichever way {v.they} fund it.</strong> If {v.they} keep the home, it counts straight away and so does the rent. If {v.they} sell, the money left over counts. The next step shows what each way does to it.</li>
                        ) : (
                            <li><strong className="text-slate-900">The entry payment {villageInputs.entryFee > EXTRA_ALLOWABLE_AMOUNT ? 'is not counted' : 'counts as an asset'}</strong> for the Age Pension{villageInputs.entryFee > EXTRA_ALLOWABLE_AMOUNT ? ', and there is no Rent Assistance' : ', and Rent Assistance may be paid on the fees'}.</li>
                        )}
                    </ul>
                    {runway && (
                        <>
                            <p className="mt-5 text-[12px] font-semibold text-brand-navy">How long the money lasts, {stay}</p>
                            <div className="mt-1">
                                <FundingRunway headline={<span className="text-[15px] text-slate-700">{runway.headline}</span>} coveredPct={runway.coveredPct} leftLabel={runway.leftLabel} rightLabel={runway.rightLabel} depleted={runway.depleted} flag={runway.flag} />
                            </div>
                        </>
                    )}
                </div>
            )}

            <p className="text-[13px] text-slate-500">
                Figures use the rates in force from {RATES_PROVENANCE.agePension}. <button type="button" onClick={onAssumptions} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2">What the figures assume</button>.
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4"><ChevronLeft size={15} /> Change the village or the contract</button>
                <button type="button" onClick={onNext} disabled={!ready} data-testid="rv-step2-next"
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-[14px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50">
                    See ways to fund the move and use what&apos;s left <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default RvStep2;
